/**
 * APEX v7.7.2 — Objectifs par champion
 *
 * Chaque champion Main/Flex peut avoir un objectif actif lié :
 *   - "60% WR sur Lucian sur 15 games"
 *   - "≥ 8 CS/min sur Caitlyn sur 10 games"
 *   - "≤ 1.5 deaths/game sur Jhin sur 10 games"
 *
 * Voir docs/PRD_OBJECTIVES_V7.7_FINAL.md section 7.
 */

function isCancelled(game) {
  if (!game) return false;
  if (game.isRemake === true) return true;
  if (typeof game.duration === 'number' && game.duration > 0 && game.duration < 5) return true;
  return false;
}

function championGames(games, championName) {
  if (!Array.isArray(games)) return [];
  return games.filter(g => !isCancelled(g) && g.champion === championName);
}

function winRate(games) {
  if (games.length === 0) return null;
  const wins = games.filter(g => g.result === 'W').length;
  return Math.round((wins / games.length) * 100);
}

function avgCsPerMin(games) {
  const withCs = games.filter(g => typeof g.csPerMin === 'number');
  if (withCs.length === 0) return null;
  return withCs.reduce((s, g) => s + g.csPerMin, 0) / withCs.length;
}

function avgDeaths(games) {
  if (games.length === 0) return null;
  return games.reduce((s, g) => s + (g.deaths || 0), 0) / games.length;
}

function avgGreed(games) {
  if (games.length === 0) return null;
  const total = games.reduce((s, g) =>
    s + (g.deathDetails || []).filter(d => d?.type === 'greed').length, 0);
  return total / games.length;
}

/**
 * Génère des suggestions d'objectifs pour un champion donné, selon ses stats récentes.
 * @returns {Array} [{ criterion, target_value, target_games, label, description, severity }]
 */
export function suggestChampionObjectives(games, championName) {
  const champGames = championGames(games, championName);
  if (champGames.length < 5) return []; // pas assez de data

  const suggestions = [];
  const wr = winRate(champGames);
  const cs = avgCsPerMin(champGames);
  const deaths = avgDeaths(champGames);
  const greed = avgGreed(champGames);

  // Suggestion 1 : reprendre confiance si WR faible
  if (wr !== null && wr < 45 && champGames.length >= 8) {
    suggestions.push({
      criterion: 'winrate',
      target_value: 55,
      target_games: 10,
      label: `Reprendre confiance sur ${championName}`,
      description: `WR actuel ${wr}% sur ${champGames.length} games. Objectif : 55% sur 10 games.`,
      severity: 'warning'
    });
  }

  // Suggestion 2 : améliorer CS si bas
  if (cs !== null && cs < 7 && champGames.length >= 5) {
    suggestions.push({
      criterion: 'cs_per_min',
      target_value: 8,
      target_games: 10,
      label: `Améliorer le CS sur ${championName}`,
      description: `CS/min actuel ${cs.toFixed(1)}. Objectif : ≥ 8 CS/min sur 10 games.`,
      severity: 'warning'
    });
  }

  // Suggestion 3 : discipline si greed élevé
  if (greed !== null && greed >= 2.5 && champGames.length >= 5) {
    suggestions.push({
      criterion: 'greed_max',
      target_value: 1.5,
      target_games: 10,
      label: `Discipline sur ${championName}`,
      description: `${greed.toFixed(1)} greed/game actuellement. Objectif : ≤ 1.5 sur 10 games.`,
      severity: 'warning'
    });
  }

  // Suggestion 4 : capitaliser si en feu
  if (wr !== null && wr > 60 && champGames.length >= 5) {
    suggestions.push({
      criterion: 'winrate',
      target_value: 60,
      target_games: 15,
      label: `Capitaliser sur ${championName}`,
      description: `WR ${wr}% — champion en feu. Maintiens 60% sur 15 games pour climb.`,
      severity: 'positive'
    });
  }

  return suggestions;
}

/**
 * Construit le record DB pour un objectif champion.
 */
export function buildChampionObjectiveRecord({ champion, criterion, target_value, target_games, label, description }) {
  return {
    type: 'champion_objective',
    status: 'active',
    target_games: target_games || 10,
    total_games: 0,
    // v7.7 fix : champs legacy NOT NULL hérités (compat schéma historique)
    title: label || `Objectif ${champion}`,
    description: description || '',
    metric_type: criterion || 'custom',
    metric_operator: 'gte',
    metric_target: target_value || 0,
    data: {
      champion,
      criterion,
      target_value,
      target_games: target_games || 10,
      label,
      description,
      progress: { games_played: 0, current_value: null }
    }
  };
}

/**
 * Évalue la progression d'un objectif champion sur l'ensemble des games.
 * Retourne { games_played, current_value, met }.
 */
export function evaluateChampionObjective(objective, games) {
  const data = objective.data || {};
  const champGames = championGames(games, data.champion);

  // On prend les N games les plus récentes (target_games)
  const recent = [...champGames]
    .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp))
    .slice(0, data.target_games || 10);

  if (recent.length === 0) {
    return { games_played: 0, current_value: null, met: false };
  }

  let currentValue = null;
  let met = false;

  switch (data.criterion) {
    case 'winrate':
      currentValue = winRate(recent);
      met = currentValue !== null && currentValue >= data.target_value;
      break;
    case 'cs_per_min':
      currentValue = avgCsPerMin(recent);
      met = currentValue !== null && currentValue >= data.target_value;
      break;
    case 'deaths_per_game':
      currentValue = avgDeaths(recent);
      met = currentValue !== null && currentValue <= data.target_value;
      break;
    case 'greed_max':
      currentValue = avgGreed(recent);
      met = currentValue !== null && currentValue <= data.target_value;
      break;
    default:
      currentValue = null;
  }

  return {
    games_played: recent.length,
    current_value: currentValue,
    met: met && recent.length >= (data.target_games || 10)
  };
}

export const CHAMPION_CRITERIA = [
  { value: 'winrate', label: 'Winrate', unit: '%', defaultTarget: 55, direction: 'min' },
  { value: 'cs_per_min', label: 'CS/min', unit: '', defaultTarget: 8, direction: 'min' },
  { value: 'deaths_per_game', label: 'Deaths/game', unit: '', defaultTarget: 4, direction: 'max' },
  { value: 'greed_max', label: 'Greed/game', unit: '', defaultTarget: 1.5, direction: 'max' }
];
